import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import axios from "axios";
import { toast } from "react-toastify";
import {
  esErrorServicioNoActivo,
  limpiarServicioActivoLocal,
} from "../../utils/servicioActivo";
import { useNavigate } from "react-router-dom";

import SelectField from "../ui/SelectField";
import TextAreaField from "../ui/TextAreaField";
import { Wrench, Package, Trash2, PlusCircle, Save, Lock, AlertTriangle } from "lucide-react";

const FormCorrectivo = ({ servicio, gestionData, canEdit = false }) => {
  const [repuestos, setRepuestos] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [selectedRepuesto, setSelectedRepuesto] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [gestionExistente, setGestionExistente] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id;
  const canEditRepuestos = servicio.estado === "Pendiente";

  useEffect(() => {
    if (!servicio) return;

    const tipo = servicio.tipo;
    const estado = servicio.estado;

    const esCorrectivo = tipo === "Correctivo";
    const esValido =
      (esCorrectivo && ["Pendiente", "Completado"].includes(estado)) ||
      (!esCorrectivo && estado === "Pendiente");

    if (!esValido) {
      toast.error(
        "⚠️ Gestión no permitida: el servicio no está en un estado válido",
      );
      localStorage.removeItem("servicioActivo");
      setTimeout(() => navigate("/my_services"), 1500);
    }
  }, [servicio, navigate]);

  useEffect(() => {
    const fetchCatalogo = async () => {
      try {
        const res = await axios.get(`${API}/stock/stock-alma-servicios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCatalogo(res.data);
      } catch {
        toast.error("No se pudieron cargar los repuestos del almacén");
      }
    };
    fetchCatalogo();
  }, [API, token]);

  useEffect(() => {
    const fetchGestion = async () => {
      if (servicio.estado !== "Completado") return;
      if (gestionData) {
        setGestionExistente(gestionData);
        setObservaciones(gestionData.observaciones || "");
        setRepuestos(gestionData.repuestosUsados || []);
        return;
      }

      try {
        const res = await axios.get(
          `${API}/gestion/cronograma/${servicio._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.data?.length) {
          const g = res.data[0];
          setGestionExistente(g);
          setObservaciones(g.observaciones || "");
          setRepuestos(g.repuestosUsados || []);
        }
      } catch {
        toast.error("No se pudo cargar la gestión registrada");
      }
    };
    fetchGestion();
  }, [API, token, servicio, gestionData]);

  const agregarRepuesto = () => {
    const rep = catalogo.find((r) => r._id === selectedRepuesto);
    if (!rep) return toast.warning("Seleccione un repuesto válido");
    if (!rep.almacenId)
      return toast.error("Este repuesto no tiene almacén asociado");
    if (repuestos.some((r) => (r.repuestoId || r._id) === rep._id))
      return toast.info("Este repuesto ya fue agregado");

    setRepuestos((prev) => [
      ...prev,
      { ...rep, almacenId: rep.almacenId, cantidad_usada: 1 },
    ]);

    setSelectedRepuesto("");
    toast.success("Repuesto agregado");
  };

  const eliminarRepuesto = (id) => {
    setRepuestos((prev) => prev.filter((r) => (r._id || r.repuestoId) !== id));
  };

  const handleGuardar = async () => {
    if (!servicio?._id) {
      toast.error("No se encontró el ID del cronograma asociado");
      return;
    }

    setIsLoading(true);
    try {
      const payloadBase = {
        cronograma_id: servicio._id,
        tipo: "Correctivo",
        observaciones,
        usuarioId: userId,
      };

      if (servicio.estado === "Completado" && gestionExistente?._id) {
        await axios.put(`${API}/gestion/${gestionExistente._id}`, payloadBase, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("✅ Servicio correctivo actualizado");
        setTimeout(() => navigate("/my_services"), 1500);
      } else {
        const confirmado = window.confirm(
          "Al guardar esta gestión, los repuestos utilizados no se podrán modificar posteriormente. ¿Deseas continuar?",
        );
        if (!confirmado) {
          setIsLoading(false);
          return;
        }

        const payload = {
          ...payloadBase,
          repuestosUsados: repuestos.map((r) => ({
            repuestoId: r.repuestoId || r._id,
            articulo: r.articulo,
            codigo_item: r.codigo_item,
            cantidad_usada: r.cantidad_usada || 1,
            almacenId: r.almacenId,
          })),
        };

        await axios.post(`${API}/gestion`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        localStorage.removeItem("servicioActivo");
        toast.success("✅ Servicio correctivo guardado correctamente");
        setTimeout(() => navigate("/my_services"), 1500);
      }
    } catch (err) {
      if (esErrorServicioNoActivo(err)) limpiarServicioActivoLocal();
      toast.error(
        err.response?.data?.message || "Error al guardar el servicio",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* Título sección */}
      <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100">
        <div className="bg-orange-100 rounded-lg p-1.5">
          <Wrench className="w-4 h-4 text-orange-600" />
        </div>
        <h3 className="text-base font-semibold text-gray-800">
          Servicio correctivo
        </h3>
      </div>

      {/* Banners de info */}
      {servicio.estado === "Completado" && gestionExistente && (
        <p className="text-sm italic text-gray-500 bg-gray-50 rounded-xl px-4 py-2.5">
          Mostrando la gestión previamente registrada.
        </p>
      )}

      {servicio.estado === "Completado" && canEdit && (
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Los repuestos quedan bloqueados después del primer guardado y no se
          pueden modificar en edición.
        </div>
      )}

      {/* ── Repuestos ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">
              Repuestos utilizados
            </h3>
          </div>
          {repuestos.length > 0 && (
            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
              {repuestos.length}
            </span>
          )}
        </div>

        {canEditRepuestos && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end bg-gray-50 rounded-xl p-3">
            <div className="w-full sm:flex-1">
              <SelectField
                label="Seleccionar repuesto"
                name="repuesto"
                value={selectedRepuesto}
                options={catalogo}
                onChange={(e) => setSelectedRepuesto(e.target.value)}
                displayFn={(r) =>
                  `${r.articulo} (${r.precio_unitario ? `$ ${Number(r.precio_unitario).toLocaleString()}` : "—"}) (${r.stock_actual} disponibles)`
                }
              />
            </div>
            <Button
              onClick={agregarRepuesto}
              className="flex items-center gap-1.5 w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              <PlusCircle className="w-4 h-4" />
              Agregar
            </Button>
          </div>
        )}

        {repuestos.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Artículo</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Código</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cant.</th>
                  {canEditRepuestos && (
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Acción
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {repuestos.map((r, idx) => (
                  <tr
                    key={r._id || r.repuestoId}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-400 text-xs font-medium">{idx + 1}</td>
                    <td className="px-4 py-3 text-gray-800 font-medium">{r.articulo}</td>
                    <td className="px-4 py-3 text-gray-500">{r.codigo_item || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-gray-100 rounded-lg text-gray-700 font-semibold text-xs">
                        {r.cantidad_usada}
                      </span>
                    </td>
                    {canEditRepuestos && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => eliminarRepuesto(r._id || r.repuestoId)}
                          className="flex items-center gap-1 ml-auto text-red-400 hover:text-red-600 text-xs font-medium transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Quitar
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          canEditRepuestos && (
            <div className="flex flex-col items-center justify-center py-6 text-center border-2 border-dashed border-gray-200 rounded-xl">
              <Package className="w-7 h-7 text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Sin repuestos agregados</p>
            </div>
          )
        )}
      </div>

      {/* ── Observaciones ── */}
      <div className="space-y-1.5">
        <TextAreaField
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          readOnly={!canEdit}
          rows={4}
        />
        {!canEdit && (
          <p className="flex items-center gap-1.5 text-xs text-gray-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            Solo lectura
          </p>
        )}
      </div>

      {/* ── Guardar ── */}
      {canEdit && (
        <div className="pt-2 border-t border-gray-100">
          <Button
            onClick={handleGuardar}
            disabled={isLoading}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl px-6"
          >
            <Save className="w-4 h-4" />
            {isLoading ? "Guardando..." : "Guardar correctivo"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FormCorrectivo;
